import { useEffect, useState } from "react"
import Header from "@/components/layout/Header"
import Footer from "@/components/layout/Footer"
import ProductCard from "@/components/product/ProductCard"
import { getPublicProducts } from "@/api/productApi"
import { useSearchParams } from "react-router-dom"

export default function ProductListPage() {
    const [searchParams] = useSearchParams()

    const [products, setProducts] = useState<any[]>([])
    const [loading, setLoading] = useState(false)

    // 페이징 상태 관리
    const [page, setPage] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [totalElements, setTotalElements] = useState(0)
    const pageSize = 20 // 한 페이지에 노출할 상품 수 (4열 기준 3줄)

    // 상품 데이터 호출 함수
    const fetchProducts = async (targetPage = page) => {
        try {
            setLoading(true)

            // URL에서 파라미터 추출
            const keyword = searchParams.get("keyword") || undefined;
            const categoryId = searchParams.get("categoryId") ? Number(searchParams.get("categoryId")) : undefined;

            // 백엔드 API 호출
            const res = await getPublicProducts({
                page: targetPage,
                size: pageSize,
                sort: "sortOrder,asc", // 💡 기존 "createdAt,desc"에서 노출 순번 정렬로 변경!
                keyword: keyword,
                categoryId: categoryId
            })

            // 응답 데이터 구조에 맞춰 파싱 (ApiResponse 형태 고려)
            const responseData = res.data?.data || res.data || res

            setProducts(responseData.items || [])
            setTotalPages(responseData.totalPages || 0)
            setTotalElements(responseData.totalElements || 0)
            setPage(targetPage)
        } catch (error) {
            console.error("상품 목록을 불러오지 못했습니다.", error)
        } finally {
            setLoading(false)
        }
    }

    // searchParams가 변경될 때마다(검색/카테고리 클릭 시) 첫 페이지(0)부터 다시 불러오기
    useEffect(() => {
        fetchProducts(0)
    }, [searchParams])

    // 페이지 변경 핸들러
    const handlePageChange = (newPage: number) => {
        if (newPage < 0 || newPage >= totalPages) return
        fetchProducts(newPage)
        // 페이지 이동 시 스크롤을 부드럽게 맨 위로 올림
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
      <div className="flex min-h-screen flex-col bg-white">
          <Header />

          <main className="flex-1 mx-auto max-w-7xl px-4 py-12 lg:px-6 w-full mb-20">
              {/* 상단 타이틀 영역 */}
              <div className="mb-10 text-center">
                  <h2 className="text-3xl font-black tracking-tight text-slate-800">전체 상품</h2>
                  <p className="mt-3 text-sm text-slate-500">
                      MG Flower의 싱싱하고 아름다운 상품들을 만나보세요.
                  </p>
              </div>

              {/* 총 상품 건수 표시 */}
              <div className="mb-6 flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-600">
                      총 <span className="font-bold text-slate-900">{totalElements}</span>개의 상품
                  </p>
              </div>

              {/* 상품 그리드 영역 */}
              {loading ? (
                <div className="py-32 text-center text-slate-500 font-medium">
                    상품을 불러오는 중입니다...
                </div>
              ) : products.length === 0 ? (
                <div className="py-32 text-center text-slate-500 border border-slate-200 rounded-[24px] bg-slate-50">
                    등록된 상품이 없습니다.
                </div>
              ) : (
                <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 mb-16">
                    {products.map((product) => (
                      <ProductCard
                        key={product.productId}
                        product={{
                            productId: product.productId,
                            productName: product.productName,
                            price: `${product.price.toLocaleString("ko-KR")}원`,
                            badge: product.badge,
                            thumbnailUrl: product.thumbnailUrl
                        }}
                      />
                    ))}
                </div>
              )}

              {/* 페이징 네비게이션 UI */}
              {totalPages > 0 && (
                <div className="flex justify-center items-center gap-2 mt-8">
                    <button
                      disabled={page === 0 || loading}
                      onClick={() => handlePageChange(page - 1)}
                      className="h-10 px-4 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        이전
                    </button>

                    <div className="flex items-center gap-1 mx-2">
                        {Array.from({ length: totalPages }, (_, i) => i).map((p) => {
                            // 페이지가 많을 경우 축약
                            if (totalPages > 7 && Math.abs(p - page) > 2 && p !== 0 && p !== totalPages - 1) {
                                if (p === 1 || p === totalPages - 2) return <span key={p} className="text-slate-400">...</span>
                                return null
                            }
                            return (
                              <button
                                key={p}
                                disabled={loading}
                                onClick={() => handlePageChange(p)}
                                className={`h-10 w-10 rounded-full text-sm font-bold transition-colors ${
                                  p === page
                                    ? "bg-slate-900 text-white"
                                    : "bg-white text-slate-600 hover:bg-slate-100"
                                }`}
                              >
                                  {p + 1}
                              </button>
                            )
                        })}
                    </div>

                    <button
                      disabled={page >= totalPages - 1 || loading}
                      onClick={() => handlePageChange(page + 1)}
                      className="h-10 px-4 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        다음
                    </button>
                </div>
              )}
          </main>

          <Footer />
      </div>
    )
}